import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "core/LayoutContainers/DashboardLayout";

// Chart.js
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
} from "chart.js";

// Services
import userService from "../../services/user-service";
import accountService from "../../services/account-service";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip);

// Chart colors matching the new theme
const chartColors = [
  { line: "#465475", fill: "rgba(70, 84, 117, 0.2)" },
  { line: "#5a6a94", fill: "rgba(90, 106, 148, 0.2)" },
  { line: "#6e7ea3", fill: "rgba(110, 126, 163, 0.2)" },
  { line: "#8292b2", fill: "rgba(130, 146, 178, 0.2)" },
  { line: "#96a6c1", fill: "rgba(150, 166, 193, 0.2)" },
];

function Dashboard() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readings, setReadings] = useState({});
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();

  // Dialog states
  const [openAddAccount, setOpenAddAccount] = useState(false);
  const [openAddReading, setOpenAddReading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountMenu, setAccountMenu] = useState(null);

  // Form states
  const [bankInstitution, setBankInstitution] = useState("");
  const [initialBalance, setInitialBalance] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [newReading, setNewReading] = useState("");

  // Currency symbol helper
  const getCurrencySymbol = (curr) => {
    const symbols = { EUR: "€", USD: "$", GBP: "£" };
    return symbols[curr] || curr;
  };

  // Fetch readings for an account
  const fetchAccountReadings = async (id) => {
    try {
      const response = await accountService.getBankAccountReadings(id);
      return response.bankAccountReadings || [];
    } catch (err) {
      console.error("Error fetching readings:", err);
      return [];
    }
  };

  // Calculate percentage change
  const calculatePercentChange = (accountReadings) => {
    if (!accountReadings || accountReadings.length < 2) return null;
    const sorted = [...accountReadings].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
    const latest = sorted[sorted.length - 1].balance;
    const previous = sorted[sorted.length - 2].balance;
    if (previous === 0) return null;
    return ((latest - previous) / previous * 100).toFixed(1);
  };

  // Format chart data for mini charts
  const getChartData = (accountReadings, colorIndex, currencySymbol) => {
    if (!accountReadings || accountReadings.length === 0) {
      return { labels: [], data: [], rawData: [] };
    }
    const sorted = [...accountReadings].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
    const recent = sorted.slice(-8); // Last 8 readings
    const color = chartColors[colorIndex % chartColors.length];
    
    return {
      labels: recent.map(r => {
        const d = new Date(r.createdOn);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      }),
      datasets: [{
        data: recent.map(r => r.balance),
        borderColor: color.line,
        backgroundColor: color.fill,
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: color.line,
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        borderWidth: 2,
      }],
      rawData: recent,
      currencySymbol,
    };
  };

  // Get chart options with currency symbol
  const getChartOptions = (currencySymbol) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: { size: 12 },
        bodyFont: { size: 14, weight: "bold" },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${currencySymbol}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
        },
      },
    },
    scales: {
      x: { display: true, grid: { display: false }, ticks: { font: { size: 10 }, color: "#9e9e9e" } },
      y: { display: false },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  });

  // Fetch accounts on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userEmail = localStorage.getItem("userEmail");
        if (!userEmail) {
          navigate("/login");
          return;
        }
        
        // Check if userId is already in localStorage
        let userId = localStorage.getItem("userId");
        if (!userId) {
          // Only make the API call if userId is not in localStorage
          const user = await userService.getUserByEmail(userEmail);
          if (!user?.id) throw new Error("Could not resolve user");
          userId = user.id;
          // Store userId in localStorage for future use
          localStorage.setItem("userId", userId);
        }
        setUserId(userId);

        const accountsData = await accountService.getUserAccounts(userId);
        const formatted = accountsData.map(acc => ({
          id: acc.id,
          name: acc.name,
          institution: acc.institution || "",
          balance: acc.balance || 0,
          currency: acc.currency || "EUR",
        }));
        setAccounts(formatted);

        // Fetch readings for all accounts
        const readingsData = {};
        for (const acc of formatted) {
          readingsData[acc.id] = await fetchAccountReadings(acc.id);
        }
        setReadings(readingsData);
        setError(null);
      } catch (err) {
        console.error("Error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // Calculate total balance
  const totalBalance = accounts.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0);

  // Calculate overall percentage change
  const calculateOverallChange = () => {
    let totalCurrent = 0;
    let totalPrevious = 0;
    accounts.forEach(acc => {
      const accReadings = readings[acc.id] || [];
      if (accReadings.length >= 2) {
        const sorted = [...accReadings].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        totalCurrent += sorted[sorted.length - 1].balance;
        totalPrevious += sorted[sorted.length - 2].balance;
      } else {
        totalCurrent += acc.balance;
        totalPrevious += acc.balance;
      }
    });
    if (totalPrevious === 0) return null;
    return ((totalCurrent - totalPrevious) / totalPrevious * 100).toFixed(1);
  };

  // Handle add account
  const handleAddAccount = async () => {
    if (!bankInstitution.trim() || !initialBalance) return;
    try {
      const response = await accountService.createAccount(userId, {
        name: bankInstitution,
        balance: parseFloat(initialBalance),
        currency,
      });
      const newAcc = {
        id: response.id,
        name: response.name,
        institution: response.institution || bankInstitution,
        balance: response.balance || parseFloat(initialBalance),
        currency: response.currency || currency,
      };
      setAccounts(prev => [...prev, newAcc]);
      setReadings(prev => ({ ...prev, [response.id]: [] }));
      resetAddForm();
      setOpenAddAccount(false);
    } catch (err) {
      console.error("Error adding account:", err);
    }
  };

  // Handle add reading
  const handleAddReading = async () => {
    if (!newReading || !selectedAccount) return;
    try {
      await accountService.addBankAccountReading(selectedAccount.id, parseFloat(newReading));
      // Refresh readings
      const updatedReadings = await fetchAccountReadings(selectedAccount.id);
      setReadings(prev => ({ ...prev, [selectedAccount.id]: updatedReadings }));
      // Update account balance
      setAccounts(prev => prev.map(acc =>
        acc.id === selectedAccount.id ? { ...acc, balance: parseFloat(newReading) } : acc
      ));
      setNewReading("");
      setOpenAddReading(false);
      setSelectedAccount(null);
    } catch (err) {
      console.error("Error adding reading:", err);
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    try {
      await accountService.deleteAccount(userId, selectedAccount.id);
      setAccounts(prev => prev.filter(acc => acc.id !== selectedAccount.id));
      setReadings(prev => {
        const updated = { ...prev };
        delete updated[selectedAccount.id];
        return updated;
      });
      setOpenDeleteConfirm(false);
      setSelectedAccount(null);
    } catch (err) {
      console.error("Error deleting:", err);
    }
  };

  const resetAddForm = () => {
    setBankInstitution("");
    setInitialBalance("");
    setCurrency("EUR");
  };

  const openAccountMenu = (event, account) => {
    event.stopPropagation();
    setSelectedAccount(account);
    setAccountMenu(event.currentTarget);
  };

  const closeAccountMenu = () => {
    setAccountMenu(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5" color="text">Loading accounts...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h5" color="error">{error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const overallChange = calculateOverallChange();

  return (
    <DashboardLayout>
      <MDBox py={3}>
        {/* Header */}
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
          <MDBox>
            <MDTypography variant="h3" fontWeight="bold" color="dark">
              Bank Dashboard
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Track all your accounts in one place
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={() => setOpenAddAccount(true)}
            sx={{
              borderColor: "#465475",
              color: "#465475",
              "&:hover": {
                borderColor: "#3a4560",
                backgroundColor: "rgba(70, 84, 117, 0.1)",
              },
            }}
          >
            Add Account
          </MDButton>
        </MDBox>

        {/* Total Balance Card */}
        <Card sx={{ mb: 4, p: 3, background: "linear-gradient(135deg, #465475 0%, #5a6a94 100%)", borderRadius: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDBox>
              <MDBox display="flex" alignItems="center" mb={1}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={48}
                  height={48}
                  borderRadius="lg"
                  sx={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  mr={2}
                >
                  <Icon sx={{ color: "#fff", fontSize: 28 }}>account_balance_wallet</Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="button" color="white" fontWeight="medium" sx={{ opacity: 0.9 }}>
                    Total Balance
                  </MDTypography>
                  <MDTypography variant="caption" color="white" display="block" sx={{ opacity: 0.7 }}>
                    {accounts.length} account{accounts.length !== 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDTypography variant="h2" fontWeight="bold" color="white" sx={{ mt: 2 }}>
                {getCurrencySymbol(accounts[0]?.currency || "EUR")}{totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </MDTypography>
            </MDBox>
            {overallChange !== null && (
              <MDBox
                sx={{
                  backgroundColor: parseFloat(overallChange) >= 0 ? "rgba(76, 175, 80, 0.9)" : "rgba(244, 67, 54, 0.9)",
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                }}
              >
                <MDTypography variant="button" color="white" fontWeight="medium">
                  <Icon sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}>
                    {parseFloat(overallChange) >= 0 ? "trending_up" : "trending_down"}
                  </Icon>
                  {parseFloat(overallChange) >= 0 ? "+" : ""}{overallChange}%
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Account Cards Grid */}
        <Grid container spacing={3}>
          {accounts.map((account, index) => {
            const accReadings = readings[account.id] || [];
            const percentChange = calculatePercentChange(accReadings);
            const chartData = getChartData(accReadings, index, getCurrencySymbol(account.currency));
            const color = chartColors[index % chartColors.length];

            return (
              <Grid item xs={12} md={6} lg={4} key={account.id}>
                <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", height: "100%" }}>
                  {/* Card Header */}
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <MDBox display="flex" alignItems="center">
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={44}
                        height={44}
                        borderRadius="lg"
                        sx={{ backgroundColor: "rgba(70, 84, 117, 0.2)" }}
                        mr={1.5}
                      >
                        <Icon sx={{ color: "#465475", fontSize: 24 }}>account_balance</Icon>
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold" color="dark">
                          {account.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {account.institution || "Bank Account"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox display="flex" alignItems="center">
                      <Tooltip title="Add Reading">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedAccount(account);
                            setOpenAddReading(true);
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <Icon>add</Icon>
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => openAccountMenu(e, account)}
                        sx={{ color: "text.secondary" }}
                      >
                        <Icon>more_vert</Icon>
                      </IconButton>
                    </MDBox>
                  </MDBox>

                  {/* Balance */}
                  <MDTypography variant="h4" fontWeight="bold" color="dark" mb={0.5}>
                    {getCurrencySymbol(account.currency)}{parseFloat(account.balance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </MDTypography>

                  {/* Percentage Change */}
                  {percentChange !== null && (
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{ color: parseFloat(percentChange) >= 0 ? "#4caf50" : "#f44336" }}
                    >
                      {parseFloat(percentChange) >= 0 ? "+" : ""}{percentChange}% from last reading
                    </MDTypography>
                  )}

                  {/* Mini Chart */}
                  {chartData.labels.length > 0 && (
                    <MDBox mt={3} height={100}>
                      <Line data={chartData} options={getChartOptions(getCurrencySymbol(account.currency))} />
                    </MDBox>
                  )}

                  {chartData.labels.length === 0 && (
                    <MDBox mt={3} height={100} display="flex" alignItems="center" justifyContent="center">
                      <MDTypography variant="caption" color="text">
                        No readings yet. Add your first reading.
                      </MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            );
          })}

          {/* Empty State */}
          {accounts.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 6, textAlign: "center", borderRadius: 3 }}>
                <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>account_balance</Icon>
                <MDTypography variant="h5" color="text" mb={1}>
                  No accounts yet
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={3}>
                  Add your first bank account to start tracking your finances
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => setOpenAddAccount(true)}
                  sx={{
                    background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #465475 0%, #3a4560 100%)",
                    },
                  }}
                >
                  Add Account
                </MDButton>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>

      {/* Account Menu */}
      <Menu
        anchorEl={accountMenu}
        open={Boolean(accountMenu)}
        onClose={closeAccountMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => {
          closeAccountMenu();
          setOpenAddReading(true);
        }}>
          <Icon sx={{ mr: 1 }}>add_circle</Icon>
          Add Reading
        </MenuItem>
        <MenuItem onClick={() => {
          closeAccountMenu();
          setOpenDeleteConfirm(true);
        }} sx={{ color: "error.main" }}>
          <Icon sx={{ mr: 1 }}>delete</Icon>
          Delete Account
        </MenuItem>
      </Menu>

      {/* Add Account Dialog - Card Style */}
      <Dialog 
        open={openAddAccount} 
        onClose={() => { resetAddForm(); setOpenAddAccount(false); }} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          }
        }}
      >
        <MDBox p={3}>
          {/* Header with close button */}
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <MDTypography variant="h4" fontWeight="bold" color="dark">
              Add Bank Account
            </MDTypography>
            <IconButton 
              onClick={() => { resetAddForm(); setOpenAddAccount(false); }}
              sx={{ color: "text.secondary" }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          {/* Bank Name */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Bank Name
            </MDTypography>
            <TextField
              autoFocus
              placeholder="e.g., Chase Bank"
              fullWidth
              value={bankInstitution}
              onChange={(e) => setBankInstitution(e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#1A73E8" },
                  "&.Mui-focused fieldset": { borderColor: "#1A73E8", borderWidth: 2 },
                },
              }}
            />
          </MDBox>

          {/* Initial Balance */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Initial Balance
            </MDTypography>
            <TextField
              placeholder="0.00"
              type="number"
              fullWidth
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <MDTypography variant="button" fontWeight="medium" sx={{ mr: 1, color: "text.secondary" }}>
                    {getCurrencySymbol(currency)}
                  </MDTypography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#465475" },
                  "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
                },
              }}
            />
          </MDBox>

          {/* Currency */}
          <MDBox mb={4}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Currency
            </MDTypography>
            <TextField
              select
              fullWidth
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              variant="outlined"
              SelectProps={{ native: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#f8f9fa",
                  "& fieldset": { borderColor: "#e0e0e0" },
                  "&:hover fieldset": { borderColor: "#465475" },
                  "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
                },
              }}
            >
              <option value="EUR">€ EUR - Euro</option>
              <option value="USD">$ USD - US Dollar</option>
              <option value="GBP">£ GBP - British Pound</option>
            </TextField>
          </MDBox>

          {/* Add Account Button */}
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            disabled={!bankInstitution.trim() || !initialBalance}
            onClick={handleAddAccount}
            sx={{
              borderRadius: 2,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Add Account
          </MDButton>
        </MDBox>
      </Dialog>

      {/* Add Reading Dialog */}
      <Dialog open={openAddReading} onClose={() => { setOpenAddReading(false); setNewReading(""); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Balance Reading</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={2}>
            Adding reading for: <strong>{selectedAccount?.name}</strong>
          </MDTypography>
          <TextField
            autoFocus
            margin="dense"
            label="Current Balance"
            type="number"
            fullWidth
            value={newReading}
            onChange={(e) => setNewReading(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newReading) {
                handleAddReading();
              }
            }}
            InputProps={{
              startAdornment: <MDTypography variant="button" sx={{ mr: 1 }}>{getCurrencySymbol(selectedAccount?.currency || "EUR")}</MDTypography>,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f8f9fa",
                "& fieldset": { borderColor: "#e0e0e0" },
                "&:hover fieldset": { borderColor: "#465475" },
                "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAddReading(false); setNewReading(""); }}>Cancel</Button>
          <Button onClick={handleAddReading} variant="contained" disabled={!newReading}>
            Add Reading
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1">
            Are you sure you want to delete <strong>{selectedAccount?.name}</strong>? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Dashboard;
